import { useCallback, useEffect, useRef, useState } from 'react';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';

// localStorage key for the staff gesture's hide/show preference (device config, survives kiosk reloads).
export const KIOSK_IDENTITY_HIDDEN_STORAGE_KEY = 'kioskIdentityHidden';

const TAPS_TO_TOGGLE = 3;
const TAP_WINDOW_MS = 1500;

/**
 * Reads the persisted hide preference. Guarded like `getPersistedKioskId`: this is read during
 * render on the public lock screen, and many marketplace test suites partially mock
 * `persisted-values`, so a throw/missing mock here must never break unrelated tests.
 */
export function getPersistedKioskIdentityHidden(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return typeof getPersistedValue === 'function'
      ? Boolean(getPersistedValue(KIOSK_IDENTITY_HIDDEN_STORAGE_KEY, false))
      : false;
  } catch {
    return false;
  }
}

export function persistKioskIdentityHidden(hidden: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (typeof setPersistedValue === 'function') {
      setPersistedValue(KIOSK_IDENTITY_HIDDEN_STORAGE_KEY, hidden);
    }
  } catch {
    // best-effort persistence; visibility still toggles for the current session
  }
}

type UseKioskIdentityVisibility = {
  isHidden: boolean;
  registerTap: () => void;
};

/**
 * Hidden staff gesture: three taps on the kiosk identity chip (or its invisible stand-in once
 * hidden) toggles whether it's shown. Taps must land within TAP_WINDOW_MS -- the counter resets
 * after the window lapses so stray taps hours apart never accumulate into a toggle. This is the
 * single source of truth for the gesture so both lock screens behave identically.
 */
export function useKioskIdentityVisibility(): UseKioskIdentityVisibility {
  const [isHidden, setIsHidden] = useState<boolean>(getPersistedKioskIdentityHidden);
  const tapCountRef = useRef(0);
  const windowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (windowTimeoutRef.current) {
        clearTimeout(windowTimeoutRef.current);
      }
    },
    []
  );

  const registerTap = useCallback(() => {
    tapCountRef.current += 1;

    if (windowTimeoutRef.current) {
      clearTimeout(windowTimeoutRef.current);
    }

    if (tapCountRef.current < TAPS_TO_TOGGLE) {
      // reset the count once the window lapses so unrelated taps don't accumulate
      windowTimeoutRef.current = setTimeout(() => {
        tapCountRef.current = 0;
      }, TAP_WINDOW_MS);
      return;
    }

    tapCountRef.current = 0;
    setIsHidden((wasHidden) => {
      const nextHidden = !wasHidden;
      persistKioskIdentityHidden(nextHidden);
      return nextHidden;
    });
  }, []);

  return { isHidden, registerTap };
}
